import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Skull, Plant, CheckCircle, XCircle } from "@phosphor-icons/react";

const EASE = [0.16, 1, 0.3, 1];

const SIDES = {
  tobacco: {
    label: "Commercial Tobacco",
    icon: Skull,
    heading: "The chemistry of dependence.",
    points: [
      "7,000+ chemicals in every cloud of smoke",
      "Nicotine engineered for maximum addiction",
      "Tar, carbon monoxide and heavy metals",
      "600+ undisclosed industrial additives",
      "A habit that owns you",
    ],
    negative: true,
  },
  botanical: {
    label: "Sage & Spirit Blend",
    icon: Plant,
    heading: "The chemistry of intention.",
    points: [
      "10 organically grown Ayurvedic botanicals",
      "Zero nicotine — nothing to hook you",
      "Zero tobacco, zero synthetic additives",
      "Mullein base for an exceptionally smooth draw",
      "A ritual that serves you",
    ],
    negative: false,
  },
};

const ROWS = [
  { label: "Nicotine", tobacco: "Highly addictive", botanical: "0.0%" },
  { label: "Tobacco leaf", tobacco: "100% of the blend", botanical: "None" },
  { label: "Additives", tobacco: "600+ industrial", botanical: "Zero" },
  { label: "Intention", tobacco: "Compulsion", botanical: "Ceremony" },
];

const Science = () => {
  const [side, setSide] = useState("botanical");
  const active = SIDES[side];
  const Icon = active.icon;

  return (
    <section id="science" data-testid="science-section" className="relative bg-[#F5F1E8] py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#0B3B24]/60">Chapter · The Science of Smoking</span>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl text-[#0B3B24] tracking-tight leading-tight">
              Two smokes. <span className="italic">Two philosophies.</span>
            </h2>
            <p className="mt-6 text-[#0B3B24]/70 text-base leading-relaxed font-light">
              One was engineered in a laboratory to create customers for life. The other
              was composed over five millennia to create moments of clarity. Compare them yourself.
            </p>

            <div className="mt-10 inline-flex rounded-full border border-[#0B3B24]/20 p-1.5 bg-white/50" data-testid="science-toggle">
              {Object.entries(SIDES).map(([key, s]) => (
                <button
                  key={key}
                  data-testid={`science-toggle-${key}`}
                  onClick={() => setSide(key)}
                  className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] font-medium transition-colors duration-300 ${
                    side === key ? "bg-[#0B3B24] text-[#F5F1E8]" : "text-[#0B3B24]/60 hover:text-[#0B3B24]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-10 space-y-0 border-t border-[#0B3B24]/15" data-testid="science-comparison-table">
              {ROWS.map((r) => (
                <div key={r.label} className="grid grid-cols-3 gap-4 py-4 border-b border-[#0B3B24]/15 text-sm">
                  <span className="uppercase tracking-[0.12em] text-xs text-[#0B3B24]/50 self-center">{r.label}</span>
                  <span className={`self-center ${side === "tobacco" ? "text-[#0B3B24] font-medium" : "text-[#0B3B24]/40"}`}>{r.tobacco}</span>
                  <span className={`self-center ${side === "botanical" ? "text-[#0B3B24] font-medium" : "text-[#0B3B24]/40"}`}>{r.botanical}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-7 lg:pl-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={side}
                initial={{ opacity: 0, y: 30, rotate: side === "tobacco" ? -1 : 1 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.55, ease: EASE }}
                data-testid={`science-card-${side}`}
                className={`rounded-2xl p-10 lg:p-14 h-full ${
                  active.negative
                    ? "bg-[#1c1a17] text-[#F5F1E8]"
                    : "bg-[#0B3B24] text-[#F5F1E8] border border-[#C5A880]/30"
                }`}
              >
                <div className="flex items-center gap-4">
                  <Icon size={40} weight="duotone" className={active.negative ? "text-[#F5F1E8]/50" : "text-[#C5A880]"} />
                  <span className="text-xs uppercase tracking-[0.25em] opacity-60">{active.label}</span>
                </div>
                <h3 className="mt-8 font-display text-3xl lg:text-4xl tracking-tight leading-tight">{active.heading}</h3>
                <ul className="mt-10 space-y-5">
                  {active.points.map((p, i) => (
                    <motion.li
                      key={p}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.08, ease: EASE }}
                      className="flex items-start gap-4 text-base font-light leading-relaxed"
                    >
                      {active.negative ? (
                        <XCircle size={22} weight="duotone" className="text-red-300/70 shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle size={22} weight="duotone" className="text-[#C5A880] shrink-0 mt-0.5" />
                      )}
                      {p}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Science;
